/**
 * useEngineState — canonical derivation of the speedtest engine state
 * trichotomy `idle | running | complete`. Pinia-backed, so callers
 * compose it without prop-threading.
 *
 * AJ-W1-θ Path θ-A.1.a: the `complete` branch is gated on the meter's
 * `choreographyComplete` signal as well as `allComplete`. The meter's
 * `playCompletion` plays a 2.5s + 550ms farewell whirl after the last
 * metric flips to `finished`; without this gate the chassis flipped to
 * `complete` the instant `allComplete` rose, collapsing the running
 * layout while the whirl was still playing. The user read the orphaned
 * whirl as "blank/errored" because its visual context had already moved
 * on. With the gate, the chassis holds `running` for the duration of
 * the whirl — the whirl IS the running state's farewell — and only
 * advances to `complete` once the renderer signals
 * `markChoreographyComplete()`.
 *
 * Mount-resilience: `choreographyComplete` defaults to `true` (see
 * `useChoreographyStore`). On a remount onto already-finished state
 * (HMR, router-back) no `start()` ever armed the gate, so the chassis
 * paints `complete` immediately. The meter's `isRunning` rising-edge
 * watcher is the only writer that arms the gate; the
 * `playCompletion` / `playAbortReset` paths are the only writers that
 * disarm — symmetric so a meter unmount mid-run cannot strand the gate
 * armed on the next test.
 */

import { computed, type ComputedRef } from "vue";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";
import { useChoreographyStore } from "@src/stores/useChoreographyStore.ts";
import { useMetricStateMachine } from "./useMetricStateMachine.ts";

export type EngineState = "idle" | "running" | "complete";

export function useEngineState(): ComputedRef<EngineState> {
    const speedtest = useSpeedtestStore();
    const stateMachine = useMetricStateMachine();
    const choreography = useChoreographyStore();
    return computed<EngineState>(() => {
        if (stateMachine.allComplete.value) {
            // `allComplete` is the necessary condition; the meter's
            // farewell choreography (if any) is the visual gate that
            // holds the running layout until the whirl resolves.
            if (choreography.choreographyComplete) return "complete";
            return "running";
        }
        if (speedtest.isRunning) return "running";
        return "idle";
    });
}
